export type SubscriptionAccess = {
  ownerId?: number | null;
  paidOnly?: boolean;
  canWrite?: boolean;
  accessState?: string | null;
  billingState?: string | null;
  trialActive?: boolean;
  trialEndsAt?: string | null;
};

const ALLOWED_SECTIONS_REGEXP =
  /^(?:\/[a-z]{2})?\/(?:profile|payment|support)(?:\/|$)/;
const PROJECTS_ROOT_REGEXP = /^(?:\/[a-z]{2})?\/projects\/?$/;

// Routes available to users without write access (paid-only, no subscription)
export const isRestrictedAllowedRoute = (pathname: string) =>
  ALLOWED_SECTIONS_REGEXP.test(pathname) || PROJECTS_ROOT_REGEXP.test(pathname);

export const isPremiumOnlyRestrictedAccess = (
  state?: SubscriptionAccess | null
) => Boolean(state?.paidOnly && state.canWrite === false);

export const isPaymentFailed = (state?: SubscriptionAccess | null) =>
  state?.billingState === "payment_failed";

const owner = (state: SubscriptionAccess) => state.ownerId ?? "account";
const trialStartedKey = (state: SubscriptionAccess) =>
  `me-page:trial-started:${owner(state)}`;
const activeTrialShownKey = (state: SubscriptionAccess) =>
  `me-page:trial-active-shown:${owner(state)}:${state.trialEndsAt ?? "active"}`;

type PlanPrice = {
  currency: string;
  amount?: number | null;
  currencySymbol?: string | null;
};

type RenewalPlan = {
  trial?: boolean | null;
  planName?: string | null;
  currencyAmountByCurrency?: PlanPrice[] | null;
};

// Trial renews into the yearly plan; backend does not always mark it with `trial`
export const findRenewalPlan = <T extends RenewalPlan>(plans?: T[] | null) =>
  plans?.find((plan) => plan.trial) ??
  plans?.find((plan) => plan.planName === "year");

// Plans are requested for the selected currency, so the first amount is a
// fallback the same way PremiumPlans shows prices
export const formatRenewalPrice = (
  plan: RenewalPlan | undefined,
  currency: string
) => {
  const prices = plan?.currencyAmountByCurrency;
  const price =
    prices?.find(
      (item) => item.currency.toLowerCase() === currency.toLowerCase()
    ) ?? prices?.[0];

  if (price?.amount == null) return null;
  return `${price.currencySymbol ?? ""}${price.amount}`;
};

// localStorage may be unavailable (private mode, blocked site data)
const storage = {
  get(key: string) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
};

export function getTrialModal(state: SubscriptionAccess | null) {
  if (!state?.paidOnly) return null;

  if (state.trialActive && state.accessState === "trial_active") {
    storage.set(trialStartedKey(state), state.trialEndsAt || "active");

    // Active trial modal is shown once per trial
    if (storage.get(activeTrialShownKey(state))) return null;
    storage.set(activeTrialShownKey(state), "1");
    return "active" as const;
  }

  const hadTrial = Boolean(
    storage.get(trialStartedKey(state)) || state.trialEndsAt
  );

  if (
    hadTrial &&
    state.accessState === "payment_required" &&
    !isPaymentFailed(state)
  ) {
    return "ended" as const;
  }
  return null;
}
